import type { AdsAssistantPostTagDefRow } from "@/lib/adsAssistantClient";
import type { AdsFeedImportLocationRow } from "@/components/ads-assistant/adsFeedImportSectionTypes";
import type { AdsIndexScope, AdsIndexSort } from "@/components/ads-assistant/useAdsAssistantIndexFilters";
import type { AdsPlatformSelectOption } from "@/lib/orgAdsPlatformOptions";

export function AdsAssistantIndexToolbar(props: {
  platformOptions: AdsPlatformSelectOption[];
  orgLocations: AdsFeedImportLocationRow[];
  orgLocationsLoaded: boolean;
  indexScope: AdsIndexScope;
  setIndexScope: (v: AdsIndexScope) => void;
  indexFilterPlatform: string;
  setIndexFilterPlatform: (v: string) => void;
  indexLocationId: string;
  setIndexLocationId: (v: string) => void;
  indexSort: AdsIndexSort;
  setIndexSort: (v: AdsIndexSort) => void;
  indexSearchDraft: string;
  setIndexSearchDraft: (v: string) => void;
  applyIndexSearch: () => void;
  indexTagId: string;
  setIndexTagId: (v: string) => void;
  postTagDefs: AdsAssistantPostTagDefRow[];
  indexGreenOnly: boolean;
  setIndexGreenOnly: (v: boolean) => void;
  clearIndexFilters: () => void;
  disabled?: boolean;
}) {
  const {
    platformOptions,
    orgLocations,
    orgLocationsLoaded,
    indexScope,
    setIndexScope,
    indexFilterPlatform,
    setIndexFilterPlatform,
    indexLocationId,
    setIndexLocationId,
    indexSort,
    setIndexSort,
    indexSearchDraft,
    setIndexSearchDraft,
    applyIndexSearch,
    indexTagId,
    setIndexTagId,
    postTagDefs,
    indexGreenOnly,
    setIndexGreenOnly,
    clearIndexFilters,
    disabled,
  } = props;

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50/80 px-2 py-2 dark:border-gray-600 dark:bg-zinc-900/50">
      <p className="text-[10px] font-medium text-gray-700 dark:text-gray-300">Filter indexed posts</p>
      <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <label className="block text-[11px] text-gray-600 dark:text-gray-400">
          Scope
          <select
            className="mt-0.5 w-full rounded border border-gray-200 bg-white px-2 py-1 text-xs dark:border-gray-600 dark:bg-zinc-950 dark:text-gray-100"
            value={indexScope}
            disabled={disabled}
            onChange={(e) => setIndexScope(e.target.value === "mine" ? "mine" : "org")}
          >
            <option value="org">Whole org</option>
            <option value="mine">My indexed posts</option>
          </select>
        </label>
        <label className="block text-[11px] text-gray-600 dark:text-gray-400">
          Platform
          <select
            className="mt-0.5 w-full rounded border border-gray-200 bg-white px-2 py-1 text-xs dark:border-gray-600 dark:bg-zinc-950 dark:text-gray-100"
            value={indexFilterPlatform}
            disabled={disabled}
            onChange={(e) => setIndexFilterPlatform(e.target.value)}
          >
            <option value="">All platforms</option>
            {platformOptions.map((o) => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-[11px] text-gray-600 dark:text-gray-400">
          Location
          <select
            className="mt-0.5 w-full rounded border border-gray-200 bg-white px-2 py-1 text-xs dark:border-gray-600 dark:bg-zinc-950 dark:text-gray-100"
            value={indexLocationId}
            disabled={disabled || !orgLocationsLoaded}
            onChange={(e) => setIndexLocationId(e.target.value)}
          >
            <option value="">All locations</option>
            {orgLocations.map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.name}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-[11px] text-gray-600 dark:text-gray-400">
          Sort
          <select
            className="mt-0.5 w-full rounded border border-gray-200 bg-white px-2 py-1 text-xs dark:border-gray-600 dark:bg-zinc-950 dark:text-gray-100"
            value={indexSort}
            disabled={disabled}
            onChange={(e) => setIndexSort(e.target.value === "sale_units_desc" ? "sale_units_desc" : "published_at_desc")}
          >
            <option value="published_at_desc">Most recent</option>
            <option value="sale_units_desc">Most sales (units)</option>
          </select>
        </label>
        <label className="block text-[11px] text-gray-600 dark:text-gray-400">
          Tag
          <select
            className="mt-0.5 w-full rounded border border-gray-200 bg-white px-2 py-1 text-xs dark:border-gray-600 dark:bg-zinc-950 dark:text-gray-100"
            value={indexTagId}
            disabled={disabled}
            onChange={(e) => setIndexTagId(e.target.value)}
          >
            <option value="">Any tag</option>
            {postTagDefs.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-[11px] text-gray-600 dark:text-gray-400">
          <span className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={indexGreenOnly}
              disabled={disabled}
              onChange={(e) => setIndexGreenOnly(e.target.checked)}
            />
            Green (portable) only
          </span>
        </label>
      </div>
      <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-end">
        <label className="block min-w-[180px] flex-1 text-[11px] text-gray-600 dark:text-gray-400">
          Search post text
          <input
            className="mt-0.5 w-full rounded border border-gray-200 bg-white px-2 py-1 text-xs dark:border-gray-600 dark:bg-zinc-950 dark:text-gray-100"
            value={indexSearchDraft}
            disabled={disabled}
            onChange={(e) => setIndexSearchDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                applyIndexSearch();
              }
            }}
            placeholder="Substring match"
          />
        </label>
        <button
          type="button"
          disabled={disabled}
          className="rounded border border-gray-300 bg-white px-3 py-1 text-xs font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-zinc-800 dark:text-gray-100 dark:hover:bg-zinc-700"
          onClick={() => applyIndexSearch()}
        >
          Apply search
        </button>
        <button
          type="button"
          disabled={disabled}
          className="rounded border border-gray-300 bg-white px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-zinc-800 dark:text-gray-200 dark:hover:bg-zinc-700"
          onClick={() => clearIndexFilters()}
        >
          Clear filters
        </button>
      </div>
    </div>
  );
}
